<template>
  <div id="userForm" class="form">
    <v-card class="mt-2 pb-5">
      <v-card-title>{{ id ? "Edit User" : "Create User" }}</v-card-title>
      <v-card-text>
        <v-form v-if="loaded" ref="form" v-model="valid" lazy-validation>
          <v-text-field
            v-model="user.firstName"
            label="First Name"
            :required="true"
          />
          <v-text-field
            v-model="user.lastName"
            label="Last Name"
            :required="true"
          />
          <v-text-field
            v-model="user.username"
            label="User Name"
            :required="true"
          />
          <v-text-field v-model="user.phone" label="Phone" :required="true" />
          <v-text-field v-model="user.email" label="Email" :required="true" />

          <div class="mt-4 d-flex justify-space-between">
            <v-btn @click="goBack"> Back </v-btn>
            <v-btn @click="handleSubmit" color="primary">
              {{ id ? "Update" : "Save" }}
            </v-btn>
            <span></span>
          </div>
        </v-form>
      </v-card-text>
    </v-card>

    <v-card v-if="user.id" class="mt-10 pb-5">
      <v-card-title> Accessible Modules </v-card-title>
      <v-card-text>
        <v-select
          v-model="userModules"
          label="User's module"
          multiple
          chips
          :items="availableModules"
          item-text="name"
          item-value="id"
        />
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn
          @click="updateUserModules"
          :loading="isAppLoading"
          :disabled="isAppLoading"
        >
          Update
        </v-btn>
        <v-spacer />
      </v-card-actions>
    </v-card>

    <v-card v-if="user.id" class="mt-10 pb-5">
      <v-card-title> User Roles </v-card-title>
      <v-card-text>
        <v-select
          v-model="userRoles"
          label="User roles"
          multiple
          chips
          :items="groupedRoles"
          item-text="name"
          item-value="id"
        />
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn
          @click="updateUserRoles"
          :loading="isAppLoading"
          :disabled="isAppLoading"
        >
          Update
        </v-btn>
        <v-spacer />
      </v-card-actions>
    </v-card>

    <v-card v-if="user.id" class="mt-10 pb-5">
      <v-card-title> Authorized Locations </v-card-title>
      <v-card-text>
        <v-select
          v-model="userLocations"
          multiple
          chips
          label="Authorized Locations"
          :items="availableLocations"
          item-text="name"
          item-value="id"
        />
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn
          @click="updateUserLocations"
          :loading="isAppLoading"
          :disabled="isAppLoading"
        >
          Update
        </v-btn>
        <v-spacer />
      </v-card-actions>
    </v-card>
  </div>
</template>

<script>
import { getUser } from "@/services/user";
import { mapActions, mapGetters } from "vuex";
import { getAccountModules } from "@/services/module";
import {
  getAccountRoles,
} from "@/services/accountServices.js";

export default {
  props: ["id"],
  data() {
    return {
      loaded: false,
      valid: false,
      user: {
        relations: {},
        firstName: "",
        lastName: "",
        username: "",
        phone: "",
        email: "",
      },
      userModules: [],
      availableModules: [],
      userRoles: [],
      availableRoles: [],
      userLocations: [],
      availableLocations: [],
      account: null,
      editPassword: false,
    };
  },
  created() {
    this.setInstace();
  },
  computed: {
    ...mapGetters("navigation", ["navState", "isAppLoading", "globalCenter"]),
    groupedRoles() {
      if (this.availableRoles) {
        //filter roles selected by user's modules
        const filteredRoles = this.availableRoles.filter(r => r.moduleId == null || this.userModules.includes(r.moduleId));
        const clone = [...filteredRoles];
        clone.sort((r1, r2) => r1.moduleName > r2.moduleName ? 1 : -1);
        const results = [];
        let cursor = null;
        clone.forEach( (r) => {
          if(!cursor || cursor != r.moduleName){
            results.push( {header: r.moduleName });
            cursor = r.moduleName;
          }
          results.push(r)
        })
        return results;
      } else {
        return [];
      }
    },
  },
  methods: {
    ...mapActions("user", ["createUser", "updateUser"]),
    ...mapActions("messages", [
      "addMessage",
      "addErrorMessage",
      "addSuccessMessage",
    ]),

    async handleSubmit() {
      if (this.id) {
        let user = this.user;
        delete user.relations;

        this.updateUser(user)
          .then((response) => {
            console.log(response);
            this.addSuccessMessage("User was updated successfully");
            this.edit(user);
          })
          .catch(() => {
            this.addErrorMessage("Error updating User, please try again later");
          });
      } else {
        let user = this.user;
        delete user.relations;

        this.createUser(user)
          .then((response) => {
            const savedUser = response;
            this.addSuccessMessage("User was created successfully");
            this.edit(savedUser);
          })
          .catch(() => {
            this.addErrorMessage("Error creating User, please try again later");
          });
      }
    },
    goBack() {
      this.$router.go(-1);
    },
    edit(item) {
      this.$router.push({ name: "userEdit", params: { id: item.id } });
    },
    setInstace() {
      if (this.id) {
        this.user.id = this.id;
        getUser(this.id).then((response) => {
          let instance = response.data;
          for (let property in instance) {
            if (
              Object.prototype.hasOwnProperty.call(instance, property) &&
              Object.prototype.hasOwnProperty.call(this.user, property)
            ) {
              this.user[property] = instance[property];
            }
          }
          this.loaded = true;
        });
      } else {
        this.loaded = true;
      }
      this.setDependencies();
    },
    async setDependencies() {
      this.initAccount().then((account) => {
        this.account = account;
      });

      await getAccountModules().then((resp) => {
        this.availableModules = resp.data.content;
        this.loadUserModules();
      });

      this.loadAccountRoles().then(() => {
        this.loadUserRoles();
      });

      this.loadAccountLocations().then((allLocations) => {
        console.log(allLocations);
        this.loadUserLocations();
      });
    },
    async initAccount() {
      return await this.$axios
        .get("/account/get-by-id", {
          params: { accountId: this.globalCenter.accountId },
        })
        .then((resp) => {
          return resp.data;
        })
        .catch(() => {
          this.addErrorMessage("Error loading account from server");
        });
    },
    loadUserModules() {
      this.$axios("/user/module", { params: { user_id: this.user.id } }).then(
        (resp) => {
          const userMods = resp.data;
          if (userMods) {
            this.userModules = userMods.map((m) => m.id);
          }
        }
      );
    },
    loadUserRoles() {
      this.$axios("/user/roles", { params: { user_id: this.user.id } }).then(
        (resp) => {
          const userRoles = resp.data;
          if (userRoles) {
            this.userRoles = userRoles.map((m) => m.id);
          }
        }
      );
    },
    loadUserLocations() {
      this.$axios("/user/locations", {
        params: { user_id: this.user.id },
      }).then((resp) => {
        const userLocations = resp.data;
        if (userLocations) {
          this.userLocations = userLocations.map((m) => m.id);
        }
      });
    },
    async loadAccountLocations() {
      return await this.$axios("/account/get-locations").then((resp) => {
        this.availableLocations = resp.data;
        return this.availableLocations;
      });
    },
    async loadAccountRoles() {
        this.availableRoles = await getAccountRoles();

        if (this.availableModules) {
          this.availableRoles.sort( (r1, r2) => r1.name > r2.name ? -1 : 1);
          this.availableRoles.forEach((r) => {
            const module = this.availableModules.find(
              (m) => m.id == r.moduleId
            );
            r.moduleName = module ? module.name : "All Modules";
          });
        }
        return this.availableRoles;
    },
    updateUserModules() {
      console.log("updating user module...");
      const data = {
        userId: this.user.id,
        moduleIds: this.userModules ? this.userModules : [],
      };
      this.$axios.post("/user/module", data).then(() => {
        this.addSuccessMessage("User modules updated");
      });
    },
    updateUserRoles() {
      console.log("updating user roles...");
      const data = {
        userId: this.user.id,
        roleIds: this.userRoles ? this.userRoles : [],
      };
      this.$axios.post("/user/role", data).then(() => {
        this.addSuccessMessage("User roles updated");
      });
    },
    updateUserLocations() {
      console.log("updating user locations...");
      const data = {
        userId: this.user.id,
        locationIds: this.userLocations ? this.userLocations : [],
      };
      this.$axios.post("/user/location", data).then(() => {
        this.addSuccessMessage("User location updated");
      });
    },
  },
  components: {},
};
</script>
